import { RichTextInput } from '@/Ship/Components/RichText/RichTextInput'
import { renderRichTextView } from '@/Ship/Components/RichText/RichTextView'
import { StatusTag } from '@/Ship/Components/StatusTag'
import { attachUploadedFiles } from '@/Ship/Helpers/attachUploadedFiles'
import { defineView, renderDateTimeView, renderUsersView } from '@/Ship/Helpers/fieldViews'

import {
    LETTER_FILE_EXTENSIONS,
    LETTER_FILE_MAX_SIZE_MB,
    LETTER_FILE_UPLOAD_URL,
    LETTER_LABELS as L,
    LETTER_REST_URL,
} from '../Contracts/ILetter'
import { LETTER_STATUS_MAP, isLetterStatus } from '../Contracts/LetterStatus'
import { normalizeAddresses } from '../Helpers/letterAddresses'

import type { IFieldConfig } from '@/Core/Contracts/UI/Form'
import type { IFormEventContext } from '@/Core/Contracts/UI/Form/IFormConfig'
import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'

/** Лише перегляд: ставить сервер. */
const readOnly = (field: IFieldConfig): IFieldConfig => ({
    ...field,
    isEditable: false,
    inputProps: { ...(field.inputProps ?? {}), disabled: true },
})

/** Перелік адрес у перегляді — через кому, порожній — прочерк. */
const addressesView = defineView((value) => {
    const list = normalizeAddresses(value)

    return list.length > 0 ? list.join(', ') : '—'
})

/** Статус — тим самим бейджем, що в колонці вкладки (`StatusTag`). */
const statusView = defineView((value) =>
    isLetterStatus(value) ? <StatusTag label={LETTER_STATUS_MAP[value].label} color={LETTER_STATUS_MAP[value].color} /> : '—'
)

/**
 * Поля, яких бек від форми не приймає (`SPEC-013` §7.8, `letter:update`):
 * статус, дату відправки, власника й службове ставить він сам, а дату
 * відкладеної відправки пише лише `POST …/send`.
 */
const READ_ONLY_KEYS = [
    'id',
    'status',
    'scheduledAt',
    'sentAt',
    'sentBody',
    'sendError',
    'author',
    'client',
    'carrier',
    'clientContact',
    'carrierContact',
    'createdAt',
    'createdBy',
    'updatedAt',
    'updatedBy',
] as const

const prepareLetterPayload = ({ values }: IFormEventContext): void => {
    READ_ONLY_KEYS.forEach((key) => delete values[key])
}

/**
 * Лист клієнту, перевізнику чи їхньому контакту (`SPEC-013` §4.4, §9.2–§9.5;
 * легасі `Ship/Resources/CompanyEmailResource.js`, `CompanyEmailFormTemplate.jsx`).
 *
 * Власної сторінки немає (`SPEC-002` §2.2): листи живуть у вкладці «Листи»
 * карток клієнта й перевізника, а створюються лише генерацією з шаблону
 * (R-01) — тож ядрового «Додати» немає (`create: false` у правах колекції).
 * Шухляду листа малює `LetterDrawer` з полями цього ресурсу: ядрові «Зберегти»,
 * «Переглянути» й «Редагувати» там не потрібні, на їхньому місці —
 * «Відправити» (§9.4).
 *
 *  - адреси — три незалежні переліки (D-15), тегами; підказки — пошти власника
 *    й контактів (`LetterAddressInput`);
 *  - «Текст листа» — Ship `RichTextInput` з пресетом «letter» (ADR 036),
 *    перегляд — `RichTextView`; HTML ніде не вставляється напряму (Л-01);
 *  - вкладення — двокроково (G-05): `FileInput` вантажить файл сиротою на
 *    `POST /services/letter_files`, «Відправити» прикріплює його через
 *    `fileUuids` (`attachUploadedFiles`).
 *
 * Права — лише з беку (`SPEC-013` §11): `_permissions` листа (`update`,
 * `delete`, `send`); ролей ресурс не питає.
 */
export const LetterResource: IResourceConfig = {
    title: 'Листи',
    fields: {
        toWhom: {
            label: L.toWhom,
            type: 'tags',
            isRequired: true,
            view: addressesView,
        },
        cc: {
            label: L.cc,
            type: 'tags',
            view: addressesView,
        },
        bcc: {
            label: L.bcc,
            type: 'tags',
            view: addressesView,
        },
        scheduledAt: {
            label: L.scheduledAt,
            type: 'datetime',
            view: renderDateTimeView,
        },
        subject: {
            label: L.subject,
            type: 'string',
            isRequired: true,
            inputProps: { maxLength: 255 },
        },
        body: {
            label: L.body,
            input: RichTextInput,
            view: renderRichTextView,
            inputProps: { placeholder: 'Текст листа…', toolbar: 'letter' },
        },
        attachments: {
            label: L.attachments,
            type: 'file',
            isMultiple: true,
            uploadUrl: LETTER_FILE_UPLOAD_URL,
            uploadAccept: LETTER_FILE_EXTENSIONS,
            uploadMaxSizeMb: LETTER_FILE_MAX_SIZE_MB,
            showViewIcon: false,
            showEditIcon: false,
        },
        status: readOnly({
            label: L.status,
            view: statusView,
        }),
        sentAt: readOnly({
            label: L.sentAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        author: readOnly({
            label: L.author,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
        createdAt: readOnly({
            label: L.createdAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
    },
    form: {
        eventHandlers: {
            // Крок 2: нові вкладення — у `fileUuids`, поле `attachments` — геть із тіла
            onBeforeTransformSendSaveRequest: {
                letterFileUuids: attachUploadedFiles('attachments'),
            },
            onBeforeSendSaveRequest: {
                LetterPayload: prepareLetterPayload,
            },
        },
    },
    // Колонки легасі (`CompanyEmailCrud.jsx:76-84`) + «Статус» (Q13.10)
    table: {
        showColumns: ['sentAt', 'scheduledAt', 'toWhom', 'subject', 'body', 'status'],
    },
    drawer: {
        width: 600,
    },
    restUrl: LETTER_REST_URL,
}
